import { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tab,
  Tabs,
  Box,
  CircularProgress,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { toast } from "react-toastify";
import UserSignupComponent from "../../components/users/UserSignupComponent";
import UserTimeSpendComponent from "../../components/users/UserTimeSpendComponent";
import UserHelplineLogComponent from "../../components/users/UserHelplineLogComponent";
import { getActiveUsers } from "../../components/users";
import DateRangeFilter, {
  defaultDateRangeFilter,
  buildDateRangeParams,
} from "../../components/common/DateRangeFilter";
import { AdminPageLayout, AdminSectionCard, AdminAction } from "../../components/admin";

/* ─── CSV helpers ─── */
const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const downloadCsv = (filename, rows) => {
  const headers = ["Name", "Mobile", "State"];
  const csvRows = rows.map((user) =>
    [escapeCsv(user.name), escapeCsv(user.mobile), escapeCsv(user.state)].join(",")
  );
  const blob = new Blob([[headers.join(","), ...csvRows].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

function flattenActiveUsers(payload) {
  const activeUsersById = new Map();
  Object.values(payload || {}).forEach((bucket) => {
    (bucket?.users || []).forEach((user) => {
      if (user?.id != null) activeUsersById.set(user.id, user);
    });
  });
  return Array.from(activeUsersById.values());
}

/* ─── Shared tab styling (matches UserListPage / Video Dashboard) ─── */
const tabsSx = {
  "& .MuiTabs-indicator": {
    backgroundColor: "#3f7a2a",
    height: 3,
    borderRadius: "3px 3px 0 0",
  },
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 500,
    color: "#62715d",
    minHeight: 44,
    "&.Mui-selected": {
      color: "#3f7a2a",
      fontWeight: 700,
    },
  },
};

/* ─── Page component ─── */
export default function UsersStatisticsPage() {
  const [dateFilter, setDateFilter] = useState(defaultDateRangeFilter);
  const [activeUsers, setActiveUsers] = useState([]);
  const [loadingActiveUsers, setLoadingActiveUsers] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Active-users pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const paginatedUsers = useMemo(
    () => activeUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [activeUsers, page, rowsPerPage]
  );

  // Load Active Users independently whenever that tab is open (and when date range changes).
  useEffect(() => {
    if (activeTab !== 3) return;

    let cancelled = false;

    (async () => {
      setLoadingActiveUsers(true);
      try {
        const response = await getActiveUsers(buildDateRangeParams(dateFilter));
        if (cancelled) return;
        setActiveUsers(flattenActiveUsers(response.data?.data));
        setPage(0);
      } catch (error) {
        if (cancelled) return;
        setActiveUsers([]);
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to load active users",
          {
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: true,
          }
        );
      } finally {
        if (!cancelled) setLoadingActiveUsers(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTab, dateFilter]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <AdminPageLayout
      eyebrow="User Statistics"
      title="Users Statistics"
      description="Track how your rural platform is growing — signups, active usage, and helpline engagement across the year."
      actions={
        <div className="admin-toolbar">
          <DateRangeFilter value={dateFilter} onChange={setDateFilter} />
        </div>
      }
    >
      {/* ── Tab bar ── */}
      <AdminSectionCard className="admin-table-shell">
        <Box sx={{ px: 2, pt: 1, borderBottom: "1px solid rgba(39,55,24,0.08)" }}>
          <Tabs value={activeTab} onChange={handleTabChange} sx={tabsSx}>
            <Tab label="New User Signups" />
            <Tab label="Monthly Active Users" />
            <Tab label="Helpline Activity" />
            <Tab label="Active Users" />
          </Tabs>
        </Box>

        {/* ── Tab panels ── */}
        <Box sx={{ p: 3 }}>
          {/* Tab 0 – New User Signups */}
          {activeTab === 0 && <UserSignupComponent dateFilter={dateFilter} />}

          {/* Tab 1 – Monthly Active Users */}
          {activeTab === 1 && <UserTimeSpendComponent dateFilter={dateFilter} />}

          {/* Tab 2 – Helpline Activity */}
          {activeTab === 2 && <UserHelplineLogComponent dateFilter={dateFilter} />}

          {/* Tab 3 – Active Users list */}
          {activeTab === 3 && (
            <>
              <div className="chart-panel__header">
                <div>
                  <span className="chart-panel__eyebrow">Active Users</span>
                  <h2 className="chart-panel__title">Active users list</h2>
                  <p className="chart-panel__description">
                    Users active in the selected date range. Export includes name, mobile, and state only.
                  </p>
                </div>
                <AdminAction
                  variant="outline"
                  startIcon={<Download />}
                  onClick={() => downloadCsv("active_users.csv", activeUsers)}
                  disabled={!activeUsers.length || loadingActiveUsers}
                >
                  CSV
                </AdminAction>
              </div>
              {loadingActiveUsers ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={28} sx={{ color: "#3f7a2a" }} />
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Mobile</TableCell>
                          <TableCell>State</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedUsers.length ? (
                          paginatedUsers.map((user) => (
                            <TableRow key={user.id}>
                              <TableCell>{user.name || "N/A"}</TableCell>
                              <TableCell>{user.mobile || "N/A"}</TableCell>
                              <TableCell>{user.state || "N/A"}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              No active users found for this range.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={activeUsers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(+e.target.value);
                      setPage(0);
                    }}
                    sx={{
                      "& .MuiTablePagination-toolbar": {
                        borderTop: "1px solid #e7e5e4",
                      },
                    }}
                  />
                </>
              )}
            </>
          )}
        </Box>
      </AdminSectionCard>
    </AdminPageLayout>
  );
}
